import { useState } from "react";
import Papa from "papaparse";
import { useNotify } from "ra-core";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

// Manual-CSV import wizard (D53): Upload → Map Destination Fields → Skip Unwanted
// → List Name → run import_raw_leads. Writes through the live intake path (mirror
// shadows into raw_leads, D56). "Scrub" options are honor-only — ME does NO
// enrichment (D16): no provider lookups; scrub only affects dedupe/DNC handling.

const SKIP = "__skip__";

// CRM target fields the importer understands (match import_raw_leads' row shape).
const TARGETS: { key: string; label: string }[] = [
  { key: "address_line1", label: "Property Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "Zip" },
  { key: "county", label: "County" },
  { key: "apn_folio", label: "Folio / APN" },
  { key: "owner_1_name", label: "Owner Name" },
  { key: "first_name", label: "Contact First Name" },
  { key: "last_name", label: "Contact Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "mailing_address", label: "Mailing Address" },
  { key: "vintage_date", label: "Vintage Date (optional)" },
];

// naive header auto-map: exact/loose header-name match to a target key
function autoMap(headers: string[]): Record<string, string> {
  const m: Record<string, string> = {};
  for (const t of TARGETS) {
    const hit = headers.find(
      (h) =>
        h.toLowerCase().replace(/[\s_]+/g, "") === t.key.replace(/[\s_]+/g, "") ||
        h.toLowerCase().includes(t.label.toLowerCase()),
    );
    if (hit) m[t.key] = hit;
  }
  return m;
}

export function ImportWizard({
  open, onOpenChange, defaultListId, onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultListId: string | null;
  onDone: () => void;
}) {
  const notify = useNotify();
  const [step, setStep] = useState(1);
  const [filename, setFilename] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [dedupe, setDedupe] = useState("skip");
  const [scrubDnc, setScrubDnc] = useState(false);
  const [scrubMobile, setScrubMobile] = useState(false);
  const [listTitle, setListTitle] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);

  const reset = () => {
    setStep(1); setFilename(""); setHeaders([]); setRows([]); setMapping({});
    setDedupe("skip"); setScrubDnc(false); setScrubMobile(false); setListTitle(""); setResult(null);
  };
  const close = () => { onOpenChange(false); reset(); };

  const onFile = (file: File) => {
    setFilename(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const hs = res.meta.fields ?? [];
        setHeaders(hs);
        setRows(res.data);
        setMapping(autoMap(hs));
        setStep(2);
      },
      error: (err) => notify(`CSV parse failed: ${err.message}`, { type: "error" }),
    });
  };

  const run = async () => {
    setRunning(true);
    // build mapped rows: only mapped, non-empty target fields
    const mappedRows = rows.map((r) => {
      const o: Record<string, string> = {};
      for (const t of TARGETS) {
        const col = mapping[t.key];
        if (col && col !== SKIP && r[col] != null && String(r[col]).trim() !== "") o[t.key] = String(r[col]).trim();
      }
      return o;
    }).filter((o) => Object.keys(o).length > 0);

    const payload: any = {
      filename,
      dedupe,
      scrub_dnc: scrubDnc,
      scrub_mobile: scrubMobile,
      mapping,
      rows: mappedRows,
    };
    if (defaultListId) payload.list_id = defaultListId;
    else if (listTitle.trim()) payload.list_title = listTitle.trim();

    const { data, error } = await getSupabaseClient().rpc("import_raw_leads", { payload });
    setRunning(false);
    if (error) { notify(`Import failed: ${error.message}`, { type: "error" }); return; }
    setResult(data);
    setStep(5);
    notify(`Imported ${data?.rows_imported ?? 0} · ${data?.dupes_skipped ?? 0} dupes skipped`, { type: "success" });
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import raw leads from CSV — step {step > 4 ? 4 : step} of 4</DialogTitle>
        </DialogHeader>

        {/* Step 1 — Upload */}
        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Upload a CSV. New records are created through the live intake path and shadowed into raw leads.
            </p>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          </div>
        )}

        {/* Step 2 — Map Destination Fields */}
        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Map your CSV columns to CRM fields ({rows.length} rows). Unmapped fields are skipped.
            </p>
            <div className="grid grid-cols-2 gap-3 max-h-80 overflow-auto pr-1">
              {TARGETS.map((t) => (
                <div key={t.key} className="space-y-1">
                  <Label className="text-xs">{t.label}</Label>
                  <Select value={mapping[t.key] ?? SKIP} onValueChange={(v) => setMapping((m) => ({ ...m, [t.key]: v }))}>
                    <SelectTrigger className="h-8"><SelectValue placeholder="— skip —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SKIP}>— skip —</SelectItem>
                      {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 — Skip Unwanted (honor-only) */}
        {step === 3 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Deduplication &amp; scrub policy. ME does no enrichment — scrub options are honor-only.</p>
            <div className="space-y-1">
              <Label className="text-xs">Duplicate policy</Label>
              <Select value={dedupe} onValueChange={setDedupe}>
                <SelectTrigger className="h-8 w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates (stack membership)</SelectItem>
                  <SelectItem value="stack">Stack onto existing raw lead</SelectItem>
                  <SelectItem value="off">Off (still dedupes by key)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={scrubDnc} onCheckedChange={(v) => setScrubDnc(!!v)} /> Scrub Federal / Company DNC phones
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={scrubMobile} onCheckedChange={(v) => setScrubMobile(!!v)} /> Scrub mobile numbers
            </label>
            <p className="text-xs text-muted-foreground">
              No in-CRM DNC list exists yet — scrub toggles are recorded on the import but honor-only until a source is wired.
            </p>
          </div>
        )}

        {/* Step 4 — List name + (assign to campaign, deferred) */}
        {step === 4 && (
          <div className="space-y-3">
            {defaultListId ? (
              <p className="text-sm">Importing into the selected list.</p>
            ) : (
              <div className="space-y-1">
                <Label className="text-xs">List name (created if new)</Label>
                <Input value={listTitle} onChange={(e) => setListTitle(e.target.value)} placeholder="e.g. probate-2026-07" />
              </div>
            )}
            <div className="space-y-1 opacity-50">
              <Label className="text-xs">Assign to campaign</Label>
              <Input disabled placeholder="Available in S3 (Campaigns)" />
            </div>
            <div className="text-sm text-muted-foreground">{rows.length} rows ready · dedupe: <Badge variant="outline">{dedupe}</Badge></div>
          </div>
        )}

        {/* Step 5 — Result */}
        {step === 5 && result && (
          <div className="space-y-2 text-sm">
            <p className="font-medium">Import complete.</p>
            <ul className="grid grid-cols-2 gap-2">
              <li>Rows total: <b>{result.rows_total}</b></li>
              <li>Imported: <b>{result.rows_imported}</b></li>
              <li>Dupes skipped: <b>{result.dupes_skipped}</b></li>
              <li>DNC scrubbed: <b>{result.dnc_scrubbed}</b></li>
            </ul>
          </div>
        )}

        <DialogFooter>
          {step === 5 ? (
            <Button onClick={close}>Done</Button>
          ) : (
            <div className="flex w-full justify-between">
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <div className="flex gap-2">
                {step > 1 && step < 5 && <Button variant="outline" onClick={() => setStep((s) => s - 1)}>Back</Button>}
                {step >= 2 && step < 4 && <Button onClick={() => setStep((s) => s + 1)}>Next</Button>}
                {step === 4 && (
                  <Button onClick={run} disabled={running || (!defaultListId && !listTitle.trim())}>
                    {running ? "Importing…" : `Import ${rows.length} rows`}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
